import { useNostr } from '@nostrify/react';
import { queryAbortedOk } from '@/lib/queryHelpers';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';
import {
  WIKI_ARTICLE_KIND,
  WIKI_REDIRECT_KIND,
  isValidWikiArticle,
  isValidWikiRedirect,
  parseAddressPointer,
} from '@/lib/nip54';

/** Keeps only the newest event per pubkey (a pubkey may have republished edits). */
function latestPerAuthor(events: NostrEvent[]): NostrEvent[] {
  const byAuthor = new Map<string, NostrEvent>();
  for (const event of events) {
    const existing = byAuthor.get(event.pubkey);
    if (!existing || event.created_at > existing.created_at) {
      byAuthor.set(event.pubkey, event);
    }
  }
  return [...byAuthor.values()];
}

/** All versions of a wiki article (one per author) for a given normalized `d` tag. */
export function useWikiVersions(slug: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['wiki', 'versions', slug],
    queryFn: async (c) => {
      if (!slug) return [];
      const events = await queryAbortedOk(
        nostr.query(
          [{ kinds: [WIKI_ARTICLE_KIND], '#d': [slug], limit: 100 }],
          { signal: AbortSignal.any([c.signal, AbortSignal.timeout(5000)]) },
        ),
      );
      if (!events) return [];
      const valid = events.filter(isValidWikiArticle);
      return latestPerAuthor(valid).sort((a, b) => b.created_at - a.created_at);
    },
    enabled: Boolean(slug),
    staleTime: 60_000,
  });
}

/** A single version of an article, authored by a specific pubkey. */
export function useWikiArticle(slug: string | undefined, pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['wiki', 'article', slug, pubkey],
    queryFn: async (c) => {
      if (!slug || !pubkey) return null;
      const events = await queryAbortedOk(
        nostr.query(
          [{ kinds: [WIKI_ARTICLE_KIND], authors: [pubkey], '#d': [slug], limit: 5 }],
          { signal: AbortSignal.any([c.signal, AbortSignal.timeout(5000)]) },
        ),
      );
      if (!events) return null;
      const valid = events.filter(isValidWikiArticle);
      valid.sort((a, b) => b.created_at - a.created_at);
      return valid[0] ?? null;
    },
    enabled: Boolean(slug && pubkey),
    staleTime: 60_000,
  });
}

/** Recent wiki articles across all authors, deduped to the latest edit per (author, d-tag). */
export function useRecentWikiArticles(limit = 40) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['wiki', 'recent', limit],
    queryFn: async (c) => {
      const events = await queryAbortedOk(
        nostr.query(
          [{ kinds: [WIKI_ARTICLE_KIND], limit: limit * 3 }],
          { signal: AbortSignal.any([c.signal, AbortSignal.timeout(5000)]) },
        ),
      );
      if (!events) return [];
      const valid = events.filter(isValidWikiArticle);

      // Dedupe by (pubkey, d) — keep the newest edit.
      const byKey = new Map<string, NostrEvent>();
      for (const event of valid) {
        const d = event.tags.find(([n]) => n === 'd')?.[1] ?? '';
        const key = `${event.pubkey}:${d}`;
        const existing = byKey.get(key);
        if (!existing || event.created_at > existing.created_at) {
          byKey.set(key, event);
        }
      }

      return [...byKey.values()]
        .sort((a, b) => b.created_at - a.created_at)
        .slice(0, limit);
    },
    staleTime: 30_000,
  });
}

/**
 * Every wiki article your relays know about, paged backwards in time with
 * Nostr's `until` — the browsing surface for `/articles`. Unlike
 * `useRecentWikiArticles` (a single bounded window for the homepage
 * preview and the leaderboard's candidate pool), this keeps fetching
 * older history on demand and merges all pages, deduped to the newest
 * edit per (author, `d`) and sorted newest-first.
 */
export function useAllWikiArticles(pageSize = 48) {
  const { nostr } = useNostr();

  const query = useInfiniteQuery({
    queryKey: ['wiki', 'all', pageSize],
    queryFn: async ({ pageParam, signal: querySignal }) => {
      const signal = AbortSignal.any([querySignal, AbortSignal.timeout(8000)]);
      // Fetch extra per page: dedupe collapses re-edits of the same
      // article, so the raw fetch needs headroom to yield a full page.
      const filter: NostrFilter = { kinds: [WIKI_ARTICLE_KIND], limit: pageSize * 2 };
      if (pageParam) filter.until = pageParam;
      const events = await nostr.query([filter], { signal });
      return events.filter(isValidWikiArticle);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length === 0) return undefined;
      // `until` is inclusive — step back one second to avoid re-fetching
      // this page's oldest event.
      return Math.min(...lastPage.map((e) => e.created_at)) - 1;
    },
    initialPageParam: undefined as number | undefined,
    staleTime: 30_000,
  });

  const articles = useMemo(() => {
    const byKey = new Map<string, NostrEvent>();
    for (const event of (query.data?.pages ?? []).flat()) {
      const d = event.tags.find(([n]) => n === 'd')?.[1] ?? '';
      const key = `${event.pubkey}:${d}`;
      const existing = byKey.get(key);
      if (!existing || event.created_at > existing.created_at) {
        byKey.set(key, event);
      }
    }
    return [...byKey.values()].sort((a, b) => b.created_at - a.created_at);
  }, [query.data?.pages]);

  return { ...query, articles };
}

/** Looks up any NIP-54 redirects (kind 30819) filed under a given slug and returns the most recent target `d` tag. */
export function useWikiRedirect(slug: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['wiki', 'redirect', slug],
    queryFn: async (c) => {
      if (!slug) return null;
      const events = await queryAbortedOk(
        nostr.query(
          [{ kinds: [WIKI_REDIRECT_KIND], '#d': [slug], limit: 20 }],
          { signal: AbortSignal.any([c.signal, AbortSignal.timeout(5000)]) },
        ),
      );
      if (!events) return null;
      const valid = events.filter(isValidWikiRedirect).sort((a, b) => b.created_at - a.created_at);
      const redirect = valid[0];
      if (!redirect) return null;
      const aTag = redirect.tags.find(([n]) => n === 'a')?.[1];
      if (!aTag) return null;
      const pointer = parseAddressPointer(aTag);
      if (!pointer || pointer.kind !== WIKI_ARTICLE_KIND) return null;
      return pointer;
    },
    enabled: Boolean(slug),
    staleTime: 60_000,
  });
}
